import { useQuery } from "@tanstack/react-query";

import { axios } from "@/client/libs/axios";

/**
 * The optimization payload the LMS pushed before handing this student over.
 *
 * Read-only by design: the job description, baseline score and weights are
 * server-authored, and the LMS recomputes the final score from the same inputs. A
 * locally-altered baseline would only produce a number that jumps on return.
 *
 * Docs: lms-backend/docs/plans/2026-09-23-coach-resume-improvement.md
 */
export type CoachScoreComponent = {
  score?: number | null;
  weight?: number;
  dropped?: boolean;
  raw0to10?: number;
};

export type CoachOptimizationSessionDto = {
  email: string;
  job: {
    jobUuid: string;
    title: string;
    companyName: string;
    jobDescription: string;
    requiredSkills: string[];
  };
  baseResume: {
    coachResumeId: string;
    slug: string;
    username: string;
    label: string;
  } | null;
  /**
   * Résumé content the LMS derived from the student's parsed master résumé, used when
   * they have no usable Coach Resume to clone. Null when there was nothing to seed.
   */
  seedResume: Record<string, unknown> | null;
  baseline: {
    /**
     * WHAT THIS RUN STARTS FROM, and not always the master résumé's score.
     *
     * When the student has already optimized and scored a Coach Resume for this job, this is
     * THAT résumé's score and `breakdown` is its breakdown — because that is the document the
     * builder is about to reopen. Sending the master baseline here while handing back the
     * improved résumé is what made a second optimization quote "was 37" over a résumé already
     * scoring 58, and re-credit gains the student had already banked.
     */
    score: number;
    badge: string | null;
    badgeColor?: string | null;
    breakdown: Record<string, CoachScoreComponent | unknown>;
    /**
     * The master résumé's score, always — "before Coach Resume touched anything".
     *
     * Stable across however many rounds a student runs, so the story stays
     * "originally 37 → now 58 → projected 66" instead of resetting each visit.
     */
    originalScore?: number;
    /** Which of the two `score` came from. Absent on payloads from an older LMS build. */
    source?: "master" | "coachResume";
  };
  scoring: {
    tableUsed: "A" | "B" | null;
    weights: Record<string, number>;
    folds: string[];
    editableComponents: string[];
    fixedContribution: number;
    editableWeight: number;
    projectedCeiling: number;
    threshold: number;
  };
  targets: {
    missingSkills: string[];
    presentSkills: string[];
    suggestions: Array<{
      category: string;
      issue: string;
      suggestion: string;
      impact: "high" | "medium" | "low";
      currentScore?: number;
      missingSkills?: string[];
    }>;
  };
  returnOrigin: string;
  /** LMS tenant resolved from the handoff request hostname; server config supplies its URL. */
  lmsTenant?: string;
  expiresAt: string;
  ttlMinutes: number;
};

export const fetchOptimizationSession = async (jobUuid: string) => {
  const response = await axios.get<CoachOptimizationSessionDto>("/coach/optimization-session", {
    params: { jobUuid },
  });

  return response.data;
};

export const useOptimizationSession = (jobUuid: string | null) =>
  useQuery({
    queryKey: ["coach", "optimization-session", jobUuid],
    queryFn: () => fetchOptimizationSession(jobUuid as string),
    enabled: Boolean(jobUuid),
    // The payload is immutable for the life of the handoff, and re-fetching on focus
    // would 404 the moment its 30-minute TTL lapses mid-session.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
